<script setup lang="ts">
  import { X } from 'lucide-vue-next'
  import {
    DialogClose,
    DialogContent,
    type DialogContentEmits,
    type DialogContentProps,
    DialogOverlay,
    DialogPortal,
    useForwardPropsEmits,
    VisuallyHidden,
    DialogTitle,
    DialogDescription
  } from 'reka-ui'
  import { computed, type HTMLAttributes } from 'vue'
  import { cn } from '../../../lib/utils'

  const props = withDefaults(
    defineProps<
      DialogContentProps & {
        class?: HTMLAttributes['class']
        showCloseIcon?: boolean
        hideOverlay?: boolean
        overlayZIndex?: number
      }
    >(),
    {
      showCloseIcon: undefined,
      overlayZIndex: 50
    }
  )
  const emits = defineEmits<DialogContentEmits>()

  const delegatedProps = computed(() => {
    const { class: _, ...delegated } = props

    return delegated
  })

  const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <DialogPortal>
    <DialogOverlay
      v-if="!hideOverlay"
      class="fixed inset-0 grid place-items-center overflow-y-auto bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      :style="{ zIndex: overlayZIndex }"
    >
      <DialogContent
        :class="
          cn(
            'border-border bg-background relative z-50 my-8 grid w-full max-w-lg gap-4 border p-6 shadow-lg duration-200 sm:rounded-lg md:w-full',
            props.class
          )
        "
        v-bind="forwarded"
        :style="{ zIndex: overlayZIndex }"
        @pointer-down-outside="
          (event) => {
            const originalEvent = event.detail.originalEvent
            const target = originalEvent.target as HTMLElement
            if (
              originalEvent.offsetX > target.clientWidth ||
              originalEvent.offsetY > target.clientHeight
            ) {
              event.preventDefault()
            }
          }
        "
      >
        <visually-hidden>
          <dialog-title>No title</dialog-title>
          <dialog-description>No description</dialog-description>
        </visually-hidden>
        <slot />

        <DialogClose
          v-if="showCloseIcon ?? true"
          data-testid="dialog-close"
          class="hover:bg-secondary absolute right-4 top-4 rounded-md p-0.5 transition-colors hover:cursor-pointer"
        >
          <X class="size-4" />
          <span class="sr-only">Close</span>
        </DialogClose>
      </DialogContent>
    </DialogOverlay>
  </DialogPortal>
</template>
